import { colors } from '@tabbio/tokens';
import type {
  ApplicationStatus,
  CatalogJobListItem,
  JobDiscoveryDatePosted,
} from '@tabbio/types';

type CatalogStatusKey = ApplicationStatus | 'redirected' | 'submitted' | 'not_applied';

export const JOB_STATUS_COLORS: Record<CatalogStatusKey, string> = {
  applied: '#8E8E93',
  submitted: '#8E8E93',
  reviewed: '#0A84FF',
  shortlisted: '#5E5CE6',
  interview: '#34C759',
  offered: '#30B0C7',
  rejected: '#FF3B30',
  withdrawn: '#FF9500',
  redirected: colors.warning,
  not_applied: '#8E8E93',
};

export function mapDateFilterValue(
  value: string | null,
): JobDiscoveryDatePosted | undefined {
  if (value === 'today' || value === 'thisWeek' || value === 'thisMonth') {
    return value;
  }

  return undefined;
}

type CatalogJobDisplayDateFields = Pick<
  CatalogJobListItem,
  'postedAt' | 'createdAt' | 'updatedAt' | 'sourceInfo'
>;

function validDateValue(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  return Number.isNaN(new Date(value).getTime()) ? null : value;
}

export function getCatalogJobDisplayDate(item: CatalogJobDisplayDateFields) {
  return (
    validDateValue(item.postedAt) ??
    validDateValue(item.sourceInfo.sourceUpdatedAt) ??
    validDateValue(item.sourceInfo.lastSeenAt) ??
    validDateValue(item.updatedAt) ??
    item.createdAt
  );
}

function isArabicLocale(locale: string) {
  return locale.toLowerCase().startsWith('ar');
}

function formatFallbackRelativeTime(
  count: number,
  unit: 'minute' | 'hour' | 'day' | 'week' | 'month',
  locale: string,
) {
  const arabic = isArabicLocale(locale);

  if (unit === 'minute') {
    return arabic ? `قبل ${count} د` : `${count}m ago`;
  }

  if (unit === 'hour') {
    return arabic ? `قبل ${count} س` : `${count}h ago`;
  }

  if (unit === 'day') {
    return arabic ? `قبل ${count} ي` : `${count}d ago`;
  }

  if (unit === 'week') {
    return arabic ? `قبل ${count} أ` : `${count}w ago`;
  }

  return arabic ? `قبل ${count} ش` : `${count}mo ago`;
}

export function formatRelativeDate(
  value: string,
  locale: string,
  now = Date.now(),
) {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) {
    return '';
  }

  const diffMs = Math.max(now - time, 0);
  const minutes = Math.max(1, Math.round(diffMs / (60 * 1000)));
  const hasRelativeTimeFormat =
    typeof Intl !== 'undefined' &&
    typeof Intl.RelativeTimeFormat === 'function';

  if (diffMs < 60 * 1000) {
    return isArabicLocale(locale) ? 'الآن' : 'just now';
  }

  if (minutes < 60) {
    return hasRelativeTimeFormat
      ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
          -minutes,
          'minute',
        )
      : formatFallbackRelativeTime(minutes, 'minute', locale);
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return hasRelativeTimeFormat
      ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
          -hours,
          'hour',
        )
      : formatFallbackRelativeTime(hours, 'hour', locale);
  }

  const days = Math.round(hours / 24);
  if (days < 7) {
    return hasRelativeTimeFormat
      ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
          -days,
          'day',
        )
      : formatFallbackRelativeTime(days, 'day', locale);
  }

  const weeks = Math.round(days / 7);
  if (weeks < 5) {
    return hasRelativeTimeFormat
      ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
          -weeks,
          'week',
        )
      : formatFallbackRelativeTime(weeks, 'week', locale);
  }

  const months = Math.round(days / 30);
  return hasRelativeTimeFormat
    ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        -months,
        'month',
      )
    : formatFallbackRelativeTime(Math.max(1, months), 'month', locale);
}

export function humanizeJobValue(value: string | null | undefined) {
  if (!value) {
    return '';
  }

  if (value.toLowerCase() === 'fulltime') {
    return 'Full Time';
  }

  if (value.toLowerCase() === 'parttime') {
    return 'Part Time';
  }

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
